import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Employee, adjustLeaveBalance } from '../dataProvider';
import { EmployeePicker } from './EmployeePicker';

export interface AdjustBalanceDialogProps {
    open: boolean;
    onClose: () => void;
}

export const AdjustBalanceDialog: React.FC<AdjustBalanceDialogProps> = ({ open, onClose }) => {
    const notify = useNotify();
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [leaveType, setLeaveType] = useState<string>('Annual');
    const [days, setDays] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [useAllocated, setUseAllocated] = useState(false);
    const [allocated, setAllocated] = useState<string>('');

    const handleClose = () => {
        if (loading) return;
        setEmployee(null);
        setLeaveType('Annual');
        setDays('');
        setReason('');
        setUseAllocated(false);
        setAllocated('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!employee || !reason.trim()) return;

        let daysNum: number | undefined;
        let allocatedNum: number | undefined;

        if (useAllocated) {
            if (allocated === '') return;
            allocatedNum = parseFloat(allocated);
            if (isNaN(allocatedNum) || allocatedNum < 0) return;
        } else {
            if (days === '') return;
            daysNum = parseFloat(days);
            if (isNaN(daysNum)) return;
        }

        setLoading(true);
        try {
            await adjustLeaveBalance({
                employee_id: employee.employee_id,
                leave_type: leaveType,
                ...(useAllocated ? { allocated: allocatedNum } : { days: daysNum }),
                reason: reason.trim(),
            });
            notify('Balance adjusted successfully', { type: 'success' });
            handleClose();
        } catch (error: any) {
            const errorMessage = error?.body?.message || error?.message || 'Failed to adjust balance';
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const isValid = employee !== null && reason.trim() !== '' && (
        useAllocated
            ? allocated !== '' && !isNaN(parseFloat(allocated)) && parseFloat(allocated) >= 0
            : days !== '' && !isNaN(parseFloat(days))
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Adjust Leave Balance</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <EmployeePicker
                        label="Employee"
                        value={employee}
                        onChange={setEmployee}
                        disabled={loading}
                    />
                    <FormControl fullWidth disabled={loading}>
                        <InputLabel>Leave Type</InputLabel>
                        <Select
                            value={leaveType}
                            label="Leave Type"
                            onChange={(e) => setLeaveType(e.target.value)}
                        >
                            <MenuItem value="Annual">Annual</MenuItem>
                            <MenuItem value="Unpaid">Unpaid</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useAllocated}
                                onChange={(e) => setUseAllocated(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Set exact allocation"
                    />
                    {useAllocated ? (
                        <TextField
                            label="Allocated Days (set directly)"
                            type="number"
                            value={allocated}
                            onChange={(e) => setAllocated(e.target.value)}
                            inputProps={{ min: 0 }}
                            fullWidth
                            disabled={loading}
                        />
                    ) : (
                        <TextField
                            label="Days (positive to add, negative to deduct)"
                            type="number"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            fullWidth
                            disabled={loading}
                        />
                    )}
                    <TextField
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        required
                        multiline
                        rows={2}
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || loading}
                >
                    {loading ? <CircularProgress size={20} /> : 'Adjust'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
